import { Directive, HostListener, Input } from '@angular/core';

import { FALLBACK_IMAGE_SRC } from '@shared/global/globals';

/**
 * Swaps an `<img>` to a placeholder when its source fails to load — project
 * logos and other backend-served images that may be missing:
 *
 * ```html
 * <img [src]="projectLogo()" [appFallbackSrc]="FALLBACK_IMAGE_SRC" alt="…" />
 * ```
 */
@Directive({
  selector: 'img[appFallbackSrc]',
})
export class ImgFallbackDirective {
  /** Empty when the host uses the bare attribute form, hence the coalesce below. */
  @Input() appFallbackSrc = FALLBACK_IMAGE_SRC;

  @HostListener('error', ['$event'])
  onError(event: Event) {
    const image = event.target as HTMLImageElement;
    const fallback = this.appFallbackSrc || FALLBACK_IMAGE_SRC;

    // A fallback that is itself missing fires `error` again, so the placeholder
    // is applied at most once per source. `image.src` reads back as an absolute
    // URL, so the candidate is resolved the same way before comparing — and
    // comparing sources rather than latching a flag lets a newly bound image,
    // such as the header's logo after a project switch, fall back in its turn.
    if (image.src === new URL(fallback, document.baseURI).href) {
      return;
    }

    image.src = fallback;
  }
}
